import React, { useState, FormEvent, useEffect } from "react";
import { BsEye } from "react-icons/bs";
import { FiEyeOff } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../hook/useAuth"; // Ensure this is the correct import
import { FaSpinner } from "react-icons/fa";
import { motion } from "framer-motion"; // Import motion from framer-motion

interface Errors {
  phoneNumber?: string;
  password?: string;
}

const LoginCard: React.FC = () => {
  const { loginAdmin } = useUserAuth();
  const token = localStorage.getItem("R_M_t");

  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [errors, setErrors] = useState<Errors>({});
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showForm, setShowForm] = useState<boolean>(false); // State to control form visibility
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      navigate("/dashboard");
    }
  }, [token]);

  useEffect(() => {
    // Show the form after a delay
    const timer = setTimeout(() => {
      setShowForm(true);
    }, 500); // Adjust the delay as needed

    return () => clearTimeout(timer);
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Errors = {};
    const phoneRegex = /^\d{10}$/;
    const sanitizedPhoneNumber = phoneNumber.replace(/\s+/g, "");

    if (!sanitizedPhoneNumber || !phoneRegex.test(sanitizedPhoneNumber)) {
      newErrors.phoneNumber = "Please enter a valid 10-digit phone number.";
    }
    if (!password || password.length < 6) {
      newErrors.password = "Password must be at least 6 characters long.";
    }
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (validateForm()) {
      try {
        await loginAdmin.mutateAsync({
          loginId: phoneNumber,
          password: password,
        });
        navigate("/dashboard");
      } catch (error) {
        console.error("Login failed:", error);
      }
    }
  };

  return (
    <div className="flex  w-full overflow-hidden h-full justify-center items-center bg-gray-100">
      <motion.div
        className={` ${showForm?"p-8":"py-10 px-8"} relative shadow-md max-w-sm w-full`}
        initial={{ borderRadius: "0rem" ,backgroundColor:"green"}} // Initial border radius
        animate={{ borderRadius: showForm ? "1rem" : "0.5rem" ,backgroundColor:showForm?"white":"green"}} // Animate to larger radius when form is shown
        transition={{ duration: 0.5 }} // Duration of the border radius animation
      >
        {/* Logo Section */}
        <motion.div
          className="flex py-4 text-xl gap-2 items-center font-thin uppercase text-primary justify-center  absolute right-0 top-0 w-full bg-green-50 shadow-md"
          initial={{ opacity: 1, borderRadius: "0rem" ,}} // Fully visible initially
          animate={{ opacity: 1,borderRadius: showForm ? "1rem" : "0.5rem" }} // Remain fully visible
          transition={{ duration: 0.5 }} // Duration of the animation
        >
         <img src='Rent1.png ' className="h-10 w-10 p-1 bg-primary/5 shadow-lg rounded-lg"/>
          <span>
          Selvam Rental Management
          </span>
        </motion.div>

        {showForm && ( // Only show the form if showForm is true
          <motion.div
            initial={{ opacity: 0, height: 0 }} // Initial state for the form
            animate={{ opacity: 1, height: "auto" }} // Animate to this state
            exit={{ opacity: 0, height: 0 }} // Exit animation
            transition={{ duration: 0.5 }} // Duration of the animation
          >
            <motion.h2
              className="text-2xl font-semibold text-center mb-6 pt-14"
              initial={{ opacity: 0, y: 20 }} // Initial state
              animate={{ opacity: 1, y: 0 }} // Animate to this state
              transition={{ duration : 0.5, delay: 0.2 }} // Duration and delay of the animation
            >
              Login
            </motion.h2>

            <form onSubmit={handleLogin}>
              <div className="mb-4">
                <label
                  className="block text-slate-500 text-sm font-bold mb-2"
                  htmlFor="phoneNumber"
                >
                  Phone Number
                </label>
                <input
                  type="text"
                  id="phoneNumber"
                  value={phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,10}$/.test(value)) {
                      setPhoneNumber(value);
                    }
                  }}
                  className={`shadow appearance-none border rounded w-full py-2 px-3 text-primary leading-tight focus:outline-none focus:shadow-outline ${
                    errors.phoneNumber ? "border-red-500" : ""
                  }`}
                  placeholder="Enter your phone number"
                />

                {errors.phoneNumber && (
                  <p className="text-red-500 text-xs italic mt-1">
                    {errors.phoneNumber}
                  </p>
                )}
              </div>
              <div className="mb-4 relative">
                <label className="block text-slate-500 text-sm font-bold">
                  Password
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`shadow relative appearance-none border rounded w-full py-2 px-3 text-primary leading-tight focus:outline-none focus:shadow-outline ${
                    errors.password ? "border-red-500" : ""
                  }`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-0 pr-3 flex items-center text-sm leading-5 ${
                    errors.password ? "top-7 md:top-7" : "top-7 md:top-7"
                  }`}
                >
                  {showPassword ? (
                    <BsEye className="h-5 w-5 text-slate-500" />
                  ) : (
                    <FiEyeOff className="h-5 w-5 text-slate-500" />
                  )}
                </button>
                {errors.password && (
                  <p className="text-red-500 text-xs italic mt-1">
                    {errors.password}
                  </p>
                )}
              </div>

              <div className="flex flex-col w-full items-center justify-center mb-6">
                <button
                  type="submit"
                  className="bg-primary hover:bg-secondary text-white font-bold py-2 px-4 w-full rounded focus:outline-none focus:shadow-outline mb-3"
                >
                  {loginAdmin.isPending ? (
                    <div className="flex gap-2 items-center justify-center">
                      <FaSpinner className="animate-spin mr-2" /> Login...{" "}
                    </div>
                  ) : (
                    "Login"
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default LoginCard;